import { map, pick, chain, orderBy, size, isEmpty, get, isEqual, keys } from "lodash"
import { produce } from "immer"

export const FIELDS = {
  economicExposure: "economicExposure",
  positionPercentEquity: "positionPercentEquity",
  activePercentEquity: "activePercentEquity",
}

export function createAction({ position, field, value, filters }) {
  const adjustedPosition = {
    ...position,
    ...(position.exchange && { mic: position.exchange }),
  }
  const { assetClass } = adjustedPosition
  // sedol, cusip, isin are handled by the internalId
  const id = pick(adjustedPosition, ["internalId", "bloombergTicker", "figi"])

  switch (assetClass) {
    case "fixedIncome":
      id.isin = adjustedPosition.ticker
      break
    case "equity":
      id.ticker = adjustedPosition.ticker
      id.mic = adjustedPosition.mic
      break
    case "index":
      id.indexId = adjustedPosition.id
      break
    case "currency":
      // Depending on where the currency is coming from (API or instrument), it might have an id or a currency
      id.ticker = adjustedPosition.id || adjustedPosition.currency
      break
    default:
      // id is a fallback for positions not held in the original set or the rebalance
      id.ticker = adjustedPosition.ticker || adjustedPosition.id || adjustedPosition.identifier
  }
  return {
    tradeSecurity: {
      id,
      operation: getOperationFromChange({ ...adjustedPosition, field, value }),
      ...(filters && { filters }),
    },
  }
}

export function isRedundantAction(lastAction, newAction) {
  return lastAction
  && newAction
  && isEqual(lastAction.id, newAction.id)
  && isEqual(keys(lastAction.operation), keys(newAction.operation))
  && isEqual(lastAction.filters, newAction.filters)
}

export function addOrReplaceAction(originalActions = [], newAction) {
  const lastActionDetails = chain(originalActions).last().get("tradeSecurity").value()
  const newActionDetails = get(newAction, "tradeSecurity")
  return produce(originalActions, draft => {
    if (!isEmpty(originalActions) && isRedundantAction(lastActionDetails, newActionDetails)) {
      draft.pop()
    }
    draft.push(newAction)
  })
}

export function getOperationFromChange(position) {
  const { field, value } = position
  const numericalValue = parseFloat(value)
  switch (field) {
    case FIELDS.economicExposure:
      return {
        setEconomicExposure: numericalValue,
      }
    case FIELDS.activePercentEquity:
      return {
        setActivePercentEquity: numericalValue,
      }
    case FIELDS.percentEquity:
    default:
      return {
        setPercentEquity: numericalValue,
      }
  }
}

export function convertOptimizationTradesToActions(trades, field) {
  return map(trades, trade => createAction({
    position: pick(trade, ["assetClass", "internalId", "bloombergTicker", "ticker", "mic"]),
    value: trade.optimized * 100,
    field,
  }))
}

export function convertExperimentChangesToSimulateActions(changes) {
  return map(changes, (positionWithChanges) => {
    const { size: decimalPercentEquity } = positionWithChanges
    return createAction({
      position: positionWithChanges,
      value: decimalPercentEquity * 100,
      field: FIELDS.positionPercentEquity,
    })
  })
}

export function getTradeType(trade) {
  if (trade.securityPercentEquity > 0) {
    if (trade.tradePercentDelta > 0) {
      return "buy"
    } else {
      return "sell"
    }
  } else if (trade.securityPercentEquity < 0) {
    if (trade.tradePercentDelta > 0) {
      return "cover"
    } else {
      return "short"
    }
  } else if (trade.tradePercentDelta > 0) { // original === 0
    return "buy"
  } else {
    return "short"
  }
}

export function keepTrade(tradeSize) {
  // Filter out trades with a delta below 0.00005 because they will appear as 0.00% in the trades panel
  return Math.abs(tradeSize) >= 0.00005
}

export function filterAndOrderTrades(trades) {
  return chain(trades)
    .filter(trade => trade.traded || keepTrade(trade.tradePercentDelta))
    .orderBy(trade => -Math.abs(trade.tradePercentDelta))
    .value()
}

export function computeTradeBuckets(tradedPositions, allTrades) {
  const buckets = chain(tradedPositions)
    .reduce((accumulator, trade) => {
      const type = getTradeType(trade)
      accumulator[type].push(trade)
      return accumulator
    }, { buy: [], sell: [], short: [], cover: [] })
    .value()

  buckets.all = orderBy(allTrades, ["tradePercentDelta", "key"], ["desc", "asc"])
  return buckets
}

export function computeNumberOfTrades(trades) {
  return chain(trades)
    .omit("all")
    .reduce((numberOfTrades, tradeGroup) => numberOfTrades + size(tradeGroup), 0)
    .value()
}

export function formatPositionId({ mic, ticker, assetClass }) {
  if (assetClass !== "equity") { return ticker }
  return `${ticker}.${mic}`
}
